"use client";

import { Drawer, DrawerContent } from "@workspace/ui/components/drawer";
import { Dialog, DialogContent } from "@workspace/ui/components/dialog";
import { useModal } from "@/components/providers/modal-provider";
import { modalIds } from "./constants";
import { sendEvent, EventNames } from "@/lib/utils/analytics";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Button } from "@workspace/ui/components/button";
import { GraphsIcon } from "@/components/ui/icons/grpahs";
import { ChartIcon } from "@/components/ui/icons/chart-icon";
import { DiamondIcon } from "@/components/ui/icons/diamond";
import { CrossIcon } from "@/components/ui/icons/cross-icon";
import { cn } from "@workspace/ui/lib/utils";
import { QUIZ_SOURCE } from "../quiz/constants";
import { useEffect } from "react";

function ExitContent({
  onResume,
  onExit,
  isDesktop,
}: {
  onResume: () => void;
  onExit: () => void;
  isDesktop: boolean;
}) {
  return (
    <div
      className={cn(
        "flex flex-col",
        isDesktop ? "gap-10 p-8" : "gap-8 px-7 pt-[26px] pb-7"
      )}
    >
      <div className="flex flex-col flex-1 gap-[26px]">
        {isDesktop && (
          <div className="flex items-center justify-center size-[77px] rounded-full bg-[#F3F4ED]">
            <GraphsIcon className="size-16" />
          </div>
        )}
        <div className="flex flex-col gap-[26px]">
          <h2 className="title-24 lg:title-32 text-[#232221]">
            Leaving the quiz?
          </h2>
          <div className="flex flex-col gap-5">
            <p className="captitle-12 text-[#504C47]">
              You&apos;ll miss out on
            </p>
            <div className="flex flex-col gap-[18px]">
              <div className="flex items-center gap-[18px]">
                <ChartIcon bold className="w-[26px] h-[26px] shrink-0 text-[#232221]" />
                <p className="body-18 text-[#232221]">
                  Your personalized sample Inito hormone chart
                </p>
              </div>
              <div className="flex items-center gap-[18px]">
                <span className="shrink-0">
                  <DiamondIcon />
                </span>
                <p className="body-18 text-[#232221]">
                  Fertility insights tailored to your body and cycle
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={cn("flex gap-3", isDesktop ? "flex-row" : "flex-col")}>
        <Button
          variant="default"
          size="sm"
          className="flex-1 md:title-18"
          onClick={onResume}
        >
          Resume Quiz
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1 md:title-18"
          onClick={onExit}
        >
          Exit Quiz
        </Button>
      </div>
    </div>
  );
}

export function QuizExitModal() {
  const { modalId, handleCloseModal, handleOpenModal, modalContent } = useModal();
  const isOpen = modalId === modalIds.quizExit;
  const isDesktop = useMediaQuery("(min-width: 640px)");

  const handleResume = () => {
    const returnTo = modalContent?.returnToModalId ?? modalIds.quizQuestionnaire;
    handleOpenModal(returnTo, { touchpointType: modalContent?.touchpointType });
    sendEvent(EventNames.BOTTOM_SHEET_CLICKED, {
      click_text: "Resume Quiz",
      quiz_source: QUIZ_SOURCE,
      touchpoint_type: modalContent?.touchpointType,
      sheet_type: "exit",
    });
  };

  const handleDismiss = () => {
    sendEvent(EventNames.BOTTOM_SHEET_DISMISSED, {
      quiz_source: QUIZ_SOURCE,
      touchpoint_type: modalContent?.touchpointType,
      sheet_type: "exit",
    });
    handleResume();
  };

  const handleExit = () => {
    handleCloseModal();
    sendEvent(EventNames.BOTTOM_SHEET_CLICKED, {
      click_text: "Exit Quiz",
      quiz_source: QUIZ_SOURCE,
      touchpoint_type: modalContent?.touchpointType,
      sheet_type: "exit",
    });
  };

  useEffect(() => {
    if (isOpen) {
      sendEvent(EventNames.BOTTOM_SHEET_DISPLAYED, {
        quiz_source: QUIZ_SOURCE,
        touchpoint_type: modalContent?.touchpointType,
        sheet_type: "exit",
      });
    }
  }, [isOpen, modalContent?.touchpointType]);

  if (isDesktop) {
    return (
      <Dialog
        open={isOpen}
        onOpenChange={(open) => !open && handleResume()}
      >
        <DialogContent
          showCloseButton={false}
          className="!z-[52] p-0 overflow-hidden border bg-white rounded-2xl max-w-[500px] aspect-[436/420]"
          overlayClassName="!bg-transparent !backdrop-blur-none"
        >
          <button
            onClick={handleDismiss}
            aria-label="Close"
            className="absolute right-5 top-5 z-10 flex size-9 items-center justify-center rounded-full bg-black/[0.2] transition-colors"
          >
            <CrossIcon className="size-6" color="#FFFFFF" />
          </button>
          <ExitContent
            onResume={handleResume}
            onExit={handleExit}
            isDesktop={true}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => !open && handleResume()}
      nested
      handleOnly
    >
      <DrawerContent
        className="p-0 border-none bg-white !rounded-t-2xl !z-[60]"
        overlayClassName="bg-black/80 !z-[55]"
        showHandle={false}
      >
        <div className="relative">
          <button
            onClick={handleDismiss}
            aria-label="Close"
            className="absolute right-5 top-5 z-10"
          >
            <CrossIcon className="size-6" />
          </button>
          <ExitContent
            onResume={handleResume}
            onExit={handleExit}
            isDesktop={false}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
}
